import * as React from "react";
import { Archive, BookOpen, Loader2, Pause, Play, RotateCcw, Trash2 } from "lucide-react";
import { cn } from "@eduviaos/ui-kit";
import { SectionCard } from "../Section";
import { useCourseNavigation, useDeletePathway, useUpdatePathway } from "../../lib/learning/hooks";
import { isReadForPleasurePlan } from "../../lib/learning/copy";
import { useLearnLocale } from "../../lib/i18n/useLearnLocale";
import { localizedPurposeCopy } from "../../lib/i18n/intakeWizard";
import { progressPercentFromNavigation } from "../../lib/pathwayProgress";
import type { Pathway, PathwayStatus } from "../../lib/learning/types";

const ACCENT: Record<
  "violet" | "green",
  { shell: string; icon: string; bar: string; tag: string }
> = {
  violet: {
    shell: "border-primary/20 bg-gradient-to-br from-primary/12 via-primary/5 to-transparent",
    icon: "bg-primary text-primary-foreground",
    bar: "bg-primary",
    tag: "bg-primary/12 text-primary",
  },
  green: {
    shell:
      "border-emerald-500/20 bg-gradient-to-br from-emerald-500/12 via-emerald-500/5 to-transparent",
    icon: "bg-emerald-500 text-white",
    bar: "bg-emerald-500",
    tag: "bg-emerald-500/12 text-emerald-700 dark:text-emerald-400",
  },
};

export function PathwayProgressCard({
  pathway,
  accent = "violet",
  onFinishSetup,
  onOpenRoadmap,
}: {
  pathway: Pathway;
  accent?: "violet" | "green";
  /** Opens the intake wizard to finish an incomplete plan (subjects, roadmap, etc.). */
  onFinishSetup?: (pathway: Pathway) => void;
  onOpenRoadmap?: (pathway: Pathway) => void;
}) {
  const navQuery = useCourseNavigation(pathway.id, pathway.onboarding_complete);
  const pct = progressPercentFromNavigation(navQuery.data);
  const tones = ACCENT[accent];
  const { strings, locale } = useLearnLocale();
  const updateMut = useUpdatePathway();
  const deleteMut = useDeletePathway();
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const busy = updateMut.isPending || deleteMut.isPending;

  // Without this the card stays armed indefinitely after a mistaken tap, with no
  // way back other than deleting the plan or navigating away.
  React.useEffect(() => {
    if (!confirmDelete) return;
    const timer = window.setTimeout(() => setConfirmDelete(false), 4000);
    return () => window.clearTimeout(timer);
  }, [confirmDelete]);

  const setStatus = (status: PathwayStatus) =>
    updateMut.mutate({ id: pathway.id, body: { status } });

  // Every card renders the same three verbs, so a screen reader walking the
  // plan list hears "Pause, Archive, Delete" once per plan with no idea which
  // plan is which. Name each control after the plan it acts on.
  const actionLabel = (action: string) => strings.planActionOn(action, pathway.title);

  const statusLabel = !pathway.onboarding_complete
    ? strings.setupPending
    : pathway.status === "paused"
      ? strings.statusPaused
      : pathway.status === "completed"
        ? strings.statusCompleted
        : pathway.status === "archived"
          ? strings.statusArchived
          : strings.pathwayActive;

  const openRoadmap =
    pathway.onboarding_complete &&
    !isReadForPleasurePlan(pathway.purpose) &&
    pathway.roadmap_source !== null &&
    Boolean(onOpenRoadmap);

  return (
    // The card is a pointer affordance only. It must not carry role="button":
    // it contains Pause/Archive/Delete buttons, and a role="button" ancestor
    // flattens them into one control whose accessible name is the entire card
    // ("… Progress 0% Pause Archive Delete"). Keyboard and assistive tech reach
    // the roadmap through the title button below instead.
    <div
      className={cn(openRoadmap && "cursor-pointer")}
      onClick={openRoadmap ? () => onOpenRoadmap?.(pathway) : undefined}
    >
      <SectionCard
        className={cn(
          "space-y-4 p-4 sm:p-5",
          tones.shell,
          openRoadmap &&
            "transition-shadow duration-150 hover:shadow-md active:scale-[0.995] motion-reduce:transform-none",
        )}
      >
        <div className="flex items-start gap-3">
          <span className={cn("grid size-11 shrink-0 place-items-center rounded-xl", tones.icon)}>
            <BookOpen className="size-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              {openRoadmap ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenRoadmap?.(pathway);
                  }}
                  className="focus-visible:ring-ring focus-visible:ring-offset-card min-w-0 truncate rounded text-left text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                >
                  {pathway.title}
                </button>
              ) : (
                <p className="truncate text-sm font-semibold">{pathway.title}</p>
              )}
              {pathway.status === "active" && pathway.onboarding_complete && (
                <span className="rounded-full bg-emerald-400/90 px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-wide text-emerald-950">
                  {strings.pathwayActive}
                </span>
              )}
              {pathway.status !== "active" && (
                <span className="text-muted-foreground bg-muted rounded-full px-2 py-0.5 text-[0.62rem] font-semibold uppercase tracking-wide">
                  {statusLabel}
                </span>
              )}
            </div>
            <p className="text-muted-foreground mt-0.5 text-xs">
              {localizedPurposeCopy(locale, pathway.purpose).label}
            </p>
          </div>
        </div>

        {pathway.subjects.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {pathway.subjects.slice(0, 4).map((s) => (
              <span
                key={s}
                className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", tones.tag)}
              >
                {s}
              </span>
            ))}
          </div>
        )}

        {pathway.onboarding_complete && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs font-medium">
              <span className="text-muted-foreground">{strings.progress}</span>
              {navQuery.isLoading ? (
                <Loader2
                  className="text-muted-foreground size-3.5 animate-spin"
                  aria-hidden="true"
                />
              ) : pct != null ? (
                <span className="tabular-nums">{pct}%</span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </div>
            <div className="bg-muted/80 h-2 overflow-hidden rounded-full">
              {pct != null && (
                <div
                  className={cn(
                    "h-full rounded-full transition-[width] duration-300 ease-out",
                    tones.bar,
                  )}
                  style={{ width: `${pct}%` }}
                />
              )}
            </div>
          </div>
        )}

        <div
          className="border-border/50 flex flex-wrap items-center justify-end gap-1 border-t pt-2.5"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {!pathway.onboarding_complete && (
            <ActionButton
              icon={Play}
              label={strings.finishSetup}
              ariaLabel={actionLabel(strings.finishSetup)}
              onClick={() => onFinishSetup?.(pathway)}
              disabled={busy || !onFinishSetup}
              primary
            />
          )}
          {pathway.status === "active" && (
            <ActionButton
              icon={Pause}
              label={strings.pause}
              ariaLabel={actionLabel(strings.pause)}
              onClick={() => setStatus("paused")}
              disabled={busy}
            />
          )}
          {pathway.status === "paused" && (
            <ActionButton
              icon={Play}
              label={strings.resume}
              ariaLabel={actionLabel(strings.resume)}
              onClick={() => setStatus("active")}
              disabled={busy}
            />
          )}
          {pathway.status !== "archived" ? (
            <ActionButton
              icon={Archive}
              label={strings.archive}
              ariaLabel={actionLabel(strings.archive)}
              onClick={() => setStatus("archived")}
              disabled={busy}
            />
          ) : (
            <ActionButton
              icon={RotateCcw}
              label={strings.restore}
              ariaLabel={actionLabel(strings.restore)}
              onClick={() => setStatus("active")}
              disabled={busy}
            />
          )}
          {confirmDelete ? (
            <ActionButton
              icon={Trash2}
              label={strings.confirmDelete}
              ariaLabel={actionLabel(strings.confirmDelete)}
              onClick={() => deleteMut.mutate(pathway.id)}
              disabled={busy}
              destructive
            />
          ) : (
            <ActionButton
              icon={Trash2}
              label={strings.delete}
              ariaLabel={actionLabel(strings.delete)}
              onClick={() => setConfirmDelete(true)}
              disabled={busy}
            />
          )}
        </div>
      </SectionCard>
    </div>
  );
}

function ActionButton({
  icon: Icon,
  label,
  ariaLabel,
  onClick,
  disabled,
  primary,
  destructive,
}: {
  icon: typeof Play;
  label: string;
  /** Names the plan the action applies to; the visible label alone repeats per card. */
  ariaLabel?: string;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors duration-150 active:scale-95 disabled:opacity-50",
        primary && "bg-primary text-primary-foreground",
        destructive && "bg-destructive/10 text-destructive",
        !primary && !destructive && "text-muted-foreground hover:text-foreground hover:bg-muted/60",
      )}
    >
      <Icon className="size-3.5" aria-hidden="true" />
      {label}
    </button>
  );
}
